import React, {useState, useCallback, useEffect} from 'react';
import {
    Stack,
    Checkbox,
    FormControlLabel,
    Button,
    Alert,
} from '@mui/material';
import Swal from 'sweetalert2';
import withReactContent from "sweetalert2-react-content";
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import {HookFormTextField} from '../common/components';
import MobileNumberMask from '../common/components/mask/MobileNumberMask';
import ApiService from '../common/services/ApiService';
import MyButton from '../common/components/MyButton';
import MyTypography from '../common/components/MyTypography';

let SMS_MESSAGE_TEMPLATE = "From <SENDING_CLINIC>: This is confirmation that we sent your referral to <RECEIVING_CLINIC>.";
let SMS_MESSAGE_TEMPLATE_WITH_PHONE = "\nIf you do not hear from them within 3 days, please call them at <RECEIVING_CLINIC_PHONE> to discuss scheduling your appointment.";

const initialValues = {
    send_sms: false,
    smsMessage: '',
    phone: '',
};

const validationSchema = yup.object().shape({
    send_sms: yup.boolean().oneOf([true,false]),
    smsMessage: yup.string().required('SMS message is required'),
    phone: yup.string()
        .required('Phone number is required')
        .test('length', 'Phone number must have 10 digits', value => {
            const digits = value.replace(/\D/g, ''); // Remove non-digit characters
            return digits.length === 10;
        }),
});

const MySwal = withReactContent(Swal);

const getClinicOfficePhone = (clinic) => {
    try {
        let parsed = JSON.parse(clinic?.extra_info);

        if (typeof parsed === 'string') {
            parsed = JSON.parse(parsed);
        }

        if (parsed.office_phone) {
            return parsed.office_phone;
        }
    } catch(err) {}

    return null;
};

export const composeSmsMessage = (referrerClinic, receiverClinic) => {
    const referrerClinicName = referrerClinic.name;
    const receiverClinicName = receiverClinic.name;

    let message = SMS_MESSAGE_TEMPLATE
        .replace('<SENDING_CLINIC>', referrerClinicName)
        .replace('<RECEIVING_CLINIC>', receiverClinicName);

    const receiverClinicPhoneNumber = getClinicOfficePhone(receiverClinic);

    if (receiverClinicPhoneNumber) {
        message += SMS_MESSAGE_TEMPLATE_WITH_PHONE.replace('<RECEIVING_CLINIC_PHONE>', receiverClinicPhoneNumber);
    }

    return message;
};

function useSendSms() {
    const [props, setProps] = useState({loading: false, error: null});

    const mutateAsync = useCallback((postData) => {
        return new Promise((resolve, reject) => {
            setProps({loading: true, error: null});

            const {phone, smsMessage, ...otherParams} = postData;

            ApiService.sendSms(phone, smsMessage, otherParams)
                .then(
                    (res) => {
                        setProps({loading: false, error: null});
                        resolve(res);
                    },
                    (err) => {
                        const errorMessage = err?.data?.message || err?.message || 'Problem sending SMS.';

                        setProps({
                            loading: false,
                            error: errorMessage,
                        });

                        reject(err);
                    }
                );
        });
    }, []);

    useEffect(() => {
        return () => {
            setProps({loading: false, error: null});
        };
    }, []);

    return {
        ...props,
        mutateAsync,
    };
}

export function showReferralSentDialog(responseMessage, params, onSuccess, hideSms = false) {
    const {phone, smsMessage, referralUuid} = params;

    const SendSmsForm = ({phone, smsMessage}) => {
        const {mutateAsync, error, loading} = useSendSms();

        const { handleSubmit, control, setValue, watch, formState: {errors}, reset } = useForm({
            defaultValues: {
                ...initialValues,
                referral_uuid: referralUuid,
                phone,
                smsMessage: smsMessage,
            },
            resolver: yupResolver(validationSchema),
            shouldFocusError: true,
        });

        const handleOnClose = () => {
            Swal.close();
            reset();
        };

        const onSubmit = async (data) => {
            try {
                const res = await mutateAsync(data);

                reset();
                Swal.close();

                Swal.fire({
                    icon: 'success',
                    text: res.message || 'SMS Sent!',
                });
            } catch(err) {
                console.error("SMS send failed:", err);
            }
        };

        const sendSms = watch('send_sms', false);

        return  (
            <form noValidate
                  onSubmit={handleSubmit(onSubmit)}>
                <Stack direction="column"
                       spacing={2}
                       justifyContent="center"
                       alignItems="flex-start">
                    {
                        error &&
                        <Alert severity="error" sx={{width:'100%'}}>{error}</Alert>
                    }
                    <FormControlLabel
                        control={
                            <Checkbox
                                disabled={loading}
                                checked={sendSms}
                                onChange={(event) => {
                                    if (!event || !event.target) {
                                        return;
                                    }
                                    setValue('send_sms', event.target.checked);
                                }}
                            />
                        }
                        label="Send the Patient an SMS (text) Notification?"
                    />
                    {
                        sendSms
                            ? (<>
                                <HookFormTextField
                                    id="phone"
                                    name="phone"
                                    placeholder="Enter Patient Phone Number"
                                    label="Patient Phone Number"
                                    schema="phone"
                                    control={control}
                                    errors={errors}
                                    disabled={loading}
                                    InputProps={{
                                        inputComponent: MobileNumberMask,
                                    }}
                                    fullWidth
                                />
                                <HookFormTextField
                                    id="smsMessage"
                                    name="smsMessage"
                                    placeholder="Enter SMS message"
                                    label="SMS Message"
                                    schema="smsMessage"
                                    control={control}
                                    errors={errors}
                                    disabled={loading}
                                    fullWidth
                                    multiline
                                    rows={4}
                                />
                                <Stack direction="row"
                                       width="100%"
                                       justifyContent="center"
                                       alignItems="center"
                                       sx={{pt: 1}}>
                                    <MyButton
                                        type="submit"
                                        size="large"
                                        variant="contained"
                                        loading={loading}>Send SMS</MyButton>
                                </Stack>
                            </>)
                            : <Stack direction="row"
                                     width="100%"
                                     justifyContent="center"
                                     alignItems="center">
                                <Button size="large" variant="contained" onClick={handleOnClose}>OK</Button>
                            </Stack>
                    }
                </Stack>
            </form>
        );
    }

    MySwal.fire({
        icon: 'success',
        title: <MyTypography variant="h5" sx={{fontWeight: 600}}>{responseMessage || 'Referral Sent'}</MyTypography>,
        showCloseButton: hideSms,
        showConfirmButton: hideSms,
        html: !hideSms ? <SendSmsForm phone={phone} smsMessage={smsMessage} /> : null,
    }).then(onSuccess);
}
